import { Building2, Plus } from 'lucide-react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedBankInstrument } from '@/lib/paycom/types';
import { Label, PaymentMethodCard, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';

interface Props {
  accounts: SavedBankInstrument[];
  selectedId: string | null;
  onSelect: (account: SavedBankInstrument | null) => void;
  /** Hoisted up to WithdrawForm so the setup view can render outside the
   * outer `<form>` (browsers flatten nested forms). */
  onAddNew: () => void;
}

// Displays Pay.com vault US bank accounts (Plaid-linked). The parent merges
// these with locally-added (just-linked) accounts and feeds the union via
// `accounts`. Requires PayComProvider in the tree minted with the bank
// payment method type so the SDK iframe can drive Plaid Link.
export function WithdrawalBankPicker({ accounts, selectedId, onSelect, onAddNew }: Props) {
  const { state: payCom } = usePayComContext();

  if (payCom.isInitializing || (!payCom.savedCardsLoaded && !payCom.isFailed)) {
    return <WithdrawalBankPickerSkeleton />;
  }
  if (payCom.isFailed) {
    return <div className="text-sm text-danger-alt">Could not load saved bank accounts. Please refresh.</div>;
  }

  const hasAccounts = accounts.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Label>Bank account</Label>
      {hasAccounts && (
        <ul className="flex flex-col gap-2">
          {accounts.map((account) => {
            const isSelected = account.id === selectedId;
            return (
              <li key={account.id}>
                <PaymentMethodCard
                  brand={account.bankName || 'Bank'}
                  last4={account.last4}
                  selected={isSelected}
                  onClick={() => onSelect(isSelected ? null : account)}
                  trailing={<span aria-hidden="true" />}
                />
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={onAddNew}
        className={cn(
          'group flex w-full items-center gap-3 rounded-card border border-dashed border-border bg-surface/40 px-4 py-3 text-left transition-[border-color,background-color,box-shadow,transform] duration-150',
          'hover:-translate-y-0.5 hover:border-accent/60 hover:bg-surface hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
        )}
      >
        <span
          aria-hidden="true"
          className="relative flex size-9 items-center justify-center rounded-md border border-dashed border-border text-muted transition-colors group-hover:border-accent/60 group-hover:text-accent"
        >
          <Building2 className="size-4" strokeWidth={1.75} />
          <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-gradient-to-b from-accent to-accent-dark text-white shadow-card">
            <Plus className="size-2.5" strokeWidth={3} />
          </span>
        </span>
        <span className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-primary">
            {hasAccounts ? 'Link another bank account' : 'Link your bank account'}
          </span>
          <span className="text-xs text-muted leading-none mt-1">Powered by Plaid · CrownCoins</span>
        </span>
      </button>
    </div>
  );
}

// Mirrors the structure of the loaded picker so the form doesn't shift
// when the Pay.com SDK resolves saved sources.
function WithdrawalBankPickerSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
      <Skeleton className="h-3 w-32 rounded" />
      <Skeleton className="h-[56px] w-full rounded-card" />
      <Skeleton className="h-[68px] w-full rounded-card mt-0.5" />
    </div>
  );
}
